import useContent from "@/lib/useContent";

/**
 * usePageOverrides — Phase 3 helper for Phase-3 pages.
 *
 * Returns a `get(key, fallback)` reader bound to the requested CMS page,
 * plus pre-resolved hero/SEO objects that callers can spread directly
 * into <SEOHead /> and <PageHero />.
 *
 * Usage:
 *   const { get, seo, hero, sectionVisible } = usePageOverrides("experiences", {
 *     seo: { title: "...", description: "...", image: "..." },
 *     hero: {
 *       eyebrow: "Experiences",
 *       title: "Worlds you step into.",
 *       copy: "Ticketed immersive nights ...",
 *       image: IMG.redDancer,
 *       primary: { label: "Book", href: "/whats-on" },
 *       secondary: { label: "Talk to us", href: "/corporate" },
 *     },
 *   });
 */
export default function usePageOverrides(pageKey, defaults) {
  const { get } = useContent(pageKey);
  const seo = {
    title: get("seo.title", defaults?.seo?.title || ""),
    description: get("seo.description", defaults?.seo?.description || ""),
    image: defaults?.seo?.image,
  };
  const hero = {
    eyebrow: get("hero.eyebrow", defaults?.hero?.eyebrow || ""),
    title: get("hero.title", defaults?.hero?.title || ""),
    copy: get("hero.copy", defaults?.hero?.copy || ""),
    image: get("hero.image", defaults?.hero?.image || ""),
    primaryCta: {
      label: get(
        "hero.primaryCtaLabel",
        defaults?.hero?.primary?.label || ""
      ),
      to: get("hero.primaryCtaHref", defaults?.hero?.primary?.href || ""),
    },
    secondaryCta: defaults?.hero?.secondary
      ? {
          label: get(
            "hero.secondaryCtaLabel",
            defaults?.hero?.secondary?.label || ""
          ),
          to: get(
            "hero.secondaryCtaHref",
            defaults?.hero?.secondary?.href || ""
          ),
        }
      : undefined,
  };
  const sectionVisible = (key, defaultVisible = true) => {
    const v = get(`sections.visibility.${key}`, undefined);
    if (v === undefined) return defaultVisible;
    return Boolean(v);
  };
  return { get, seo, hero, sectionVisible };
}
